/**
 * 鏡像的健康狀態（desktop-app.md 6.4）。
 *
 * 採**升級式提醒**：單次失敗完全不提示，連續失敗 5 次才變紅。
 *
 * 單次失敗就彈 toast 是錯的——防毒軟體或雲端同步瞬間鎖檔很常見，
 * 使用者會很快學會忽略提示，於是真正壞掉那次也被一起忽略。
 */

export const MIRROR_FAILURE_THRESHOLD = 5;

export type MirrorState =
  /** 不是桌面版，或還沒問過。 */
  | 'unavailable'
  | 'idle'
  | 'syncing'
  /** 最近一次寫入成功。 */
  | 'ok'
  /** 有失敗，但還沒到門檻，畫面上不提示。 */
  | 'degraded'
  /** 連續失敗達到門檻，狀態列變紅。 */
  | 'stopped';

export interface MirrorHealthSnapshot {
  state: MirrorState;
  consecutiveFailures: number;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export function createMirrorHealth(
  onChange?: (snapshot: MirrorHealthSnapshot) => void,
  threshold: number = MIRROR_FAILURE_THRESHOLD,
) {
  let state: MirrorState = 'unavailable';
  let consecutiveFailures = 0;
  let lastSyncedAt: string | null = null;
  let lastError: string | null = null;

  function snapshot(): MirrorHealthSnapshot {
    return { state, consecutiveFailures, lastSyncedAt, lastError };
  }

  function set(next: MirrorState): void {
    state = next;
    onChange?.(snapshot());
  }

  return {
    snapshot,
    /** 桌面版啟動、確認鏡像可用時呼叫。 */
    markAvailable(): void {
      if (state === 'unavailable') set('idle');
    },
    markUnavailable(): void {
      consecutiveFailures = 0;
      lastError = null;
      set('unavailable');
    },
    markSyncing(): void {
      if (state === 'unavailable') return;
      set('syncing');
    },
    markSuccess(at: string): void {
      consecutiveFailures = 0;
      lastError = null;
      lastSyncedAt = at;
      set('ok');
    },
    markFailure(error: unknown): void {
      if (state === 'unavailable') return;
      consecutiveFailures += 1;
      lastError = error instanceof Error ? error.message : String(error);
      // 沒到門檻就停在 degraded：畫面上跟正常沒有差別，只有 tooltip 看得到。
      set(consecutiveFailures >= threshold ? 'stopped' : 'degraded');
    },
    reset(): void {
      consecutiveFailures = 0;
      lastError = null;
      if (state === 'stopped' || state === 'degraded') set('idle');
    },
  };
}

export type MirrorHealth = ReturnType<typeof createMirrorHealth>;

export interface MirrorStatusDescription {
  label: string;
  tone: 'muted' | 'success' | 'danger';
  detail: string;
  /** 狀態列要不要顯示重試按鈕。 */
  retryable: boolean;
}

export function describeMirror(snapshot: MirrorHealthSnapshot): MirrorStatusDescription | null {
  if (snapshot.state === 'unavailable') return null;

  if (snapshot.state === 'stopped') {
    return {
      label: '本機備份已停止',
      tone: 'danger',
      detail:
        `連續 ${snapshot.consecutiveFailures} 次寫不進「文件\\GentleTask\\gentletask.db」。` +
        '你的資料仍然是安全的，畫面上的內容都還在，只是那份可以複製走的備份檔沒有跟上。' +
        (snapshot.lastError ? `原因：${snapshot.lastError}` : ''),
      retryable: true,
    };
  }

  const when = snapshot.lastSyncedAt ? clockOf(snapshot.lastSyncedAt) : null;
  return {
    label: when ? `本機備份：${when}` : '本機備份',
    tone: snapshot.state === 'ok' ? 'success' : 'muted',
    detail: when
      ? `最後一次寫入「文件\\GentleTask\\gentletask.db」的時間是 ${when}。這個檔案可以整個複製到別台電腦。`
      : '這次開啟之後還沒有需要備份的變更。',
    retryable: false,
  };
}

function clockOf(timestamp: string): string | null {
  const at = new Date(timestamp);
  if (Number.isNaN(at.getTime())) return null;
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
}
